import { useIntl } from "react-intl";
import { QuotaLimitBanner } from "../QuotaLimitBanner";

interface ChatFooterProps {
  view: "chat" | "draft" | "export";
  inputText: string;
  isRunning: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  onInputChange: (inputText: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  canSend: boolean;
  initialized: boolean;
  reason: "institution_quota_exceeded" | "no_quota_configured" | null;
  quotaError: string | null;
}

export const ChatFooter = ({
  view,
  inputText,
  isRunning,
  textareaRef,
  onInputChange,
  onSubmit,
  onKeyDown,
  canSend,
  initialized,
  reason,
  quotaError,
}: ChatFooterProps) => {
  const intl = useIntl();

  const isLimitReached = initialized && !canSend;
  const isConnectError = !initialized && !!quotaError;

  const getLimitTitle = () => {
    if (isConnectError) return intl.formatMessage({ id: "app.toolboxChat.quota.connectError" });
    if (reason === "no_quota_configured")
      return intl.formatMessage({ id: "app.toolboxChat.quota.notConfigured.title" });
    return intl.formatMessage({ id: "app.toolboxChat.quota.institutionExceeded.title" });
  };

  const getLimitBody = () => {
    if (isConnectError) return null;
    if (reason === "no_quota_configured")
      return intl.formatMessage({ id: "app.toolboxChat.quota.notConfigured.body" });
    return intl.formatMessage({ id: "app.toolboxChat.quota.institutionExceeded.body" });
  };

  return (
    <div
      className={`tcw-footer${
        view === "draft" || view === "export" ? " tcw-footer--hidden" : ""
      }`}
    >
      {isLimitReached || isConnectError ? (
        <QuotaLimitBanner title={getLimitTitle()} body={getLimitBody()} />
      ) : (
        <form className="tcw-input-wrapper" onSubmit={onSubmit}>
          <textarea
            ref={textareaRef}
            className="tcw-input"
            rows={2}
            placeholder={
              !initialized
                ? intl.formatMessage({ id: "app.toolboxChat.quota.loading" })
                : intl.formatMessage({ id: "app.toolboxChat.input.placeholder" })
            }
            value={inputText}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={isRunning || !canSend}
            aria-label={intl.formatMessage({ id: "app.toolboxChat.input.ariaLabel" })}
          />
          <div className="tcw-send-wrapper">
            <button
              type="submit"
              className="tcw-send-btn"
              disabled={isRunning || !inputText.trim() || !canSend}
              aria-label={intl.formatMessage({ id: "app.toolboxChat.send.ariaLabel" })}
            >
              <span className="svg-icon tcw-send-btn-icon"></span>
            </button>
          </div>
        </form>
      )}
      <p className="tcw-disclaimer">
        {intl.formatMessage({ id: "app.toolboxChat.disclaimer" })}
        <br />
        {intl.formatMessage({ id: "app.toolboxChat.disclaimer.reloadNote" })}
      </p>
    </div>
  );
};
